import { useGetFunctionalGroups } from "@hooks";
import { Document } from "@models";
import { Chip, TextField, Typography } from "@mui/material";
import { FormData, RegulatoryTypeEnum } from "@types";
import {
  getFunctionalGroupLabelMap,
  getFunctionalGroupOptions,
} from "@utils";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import GenericSearchSelect from "src/components/GenericSearchSelect";

const DocMetadata = ({
  selectedDoc,
  formData,
  isEditMode,
  setFormData,
}: {
  selectedDoc: Document;
  formData: FormData;
  isEditMode: boolean;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) => {
  const { orgId = "" } = useParams();
  const { data: functionalGroups } = useGetFunctionalGroups(orgId);

  const functionalGroupOptions = useMemo(
    () => getFunctionalGroupOptions(functionalGroups),
    [functionalGroups]
  );
  const functionalGroupLabelMap = useMemo(
    () => getFunctionalGroupLabelMap(functionalGroups),
    [functionalGroups]
  );

  const titleNotSaved = !selectedDoc.title;

  useEffect(() => {
    setFormData((prev) => {
      const nextMetadata = {
        ...prev.documentMetadata,
      };
      let changed = false;

      if (!nextMetadata.title && selectedDoc.title) {
        nextMetadata.title = selectedDoc.title;
        changed = true;
      }

      if (!nextMetadata.regulatoryType && selectedDoc.regulatoryType) {
        nextMetadata.regulatoryType = selectedDoc.regulatoryType;
        changed = true;
      }

      if (!nextMetadata.functionalGroupId && selectedDoc.functionalGroupId) {
        nextMetadata.functionalGroupId = selectedDoc.functionalGroupId;
        changed = true;
      }

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        documentMetadata: nextMetadata,
      };
    });
  }, [selectedDoc, setFormData]);

  const handleSelect = ({
    key,
    value,
  }: {
    key: "regulatoryType" | "functionalGroupId" | "title";
    value: string | null;
  }) => {
    if (value) {
      setFormData({
        ...formData,
        documentMetadata: {
          ...formData.documentMetadata,
          [key]: value,
        },
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      documentMetadata: {
        ...formData.documentMetadata,
        title: e.target.value,
      },
    });
  };

  return (
    <div>
      <Typography variant="body1" fontWeight="bold" className="pb-2">
        Regulatory Document Information
      </Typography>
      {isEditMode ? (
        <>
          <div className="flex gap-3 items-end pb-3">
            <GenericSearchSelect
              value={formData.documentMetadata.functionalGroupId}
              setValue={(value) =>
                handleSelect({
                  key: "functionalGroupId",
                  value: value,
                })
              }
              options={functionalGroupOptions}
              getOptionLabel={(option) =>
                functionalGroupLabelMap[option] || option
              }
              label="Functional Group"
            />
            <GenericSearchSelect
              value={formData.documentMetadata.regulatoryType}
              setValue={(value) =>
                handleSelect({
                  key: "regulatoryType",
                  value: value,
                })
              }
              options={Object.values(RegulatoryTypeEnum)}
              label="Document Type"
              // TODO, remove this when we support creation of RCDs
              //   - First create a template
              //   - Then create document
              // should also tie this to canDocHaveMultipleInstances
              disabled={selectedDoc.regulatoryType === RegulatoryTypeEnum.RCD}
              getOptionDisabled={(option) => option === RegulatoryTypeEnum.RCD}
            />
          </div>
          {titleNotSaved && (
            <TextField
              label="Document Name"
              size="small"
              fullWidth
              value={formData.documentMetadata.title}
              onChange={handleChange}
            />
          )}
        </>
      ) : (
        <div className="flex gap-2 pb-3">
          {formData.documentMetadata.functionalGroupId && (
            <Chip
              label={
                functionalGroupLabelMap[
                  formData.documentMetadata.functionalGroupId
                ] || formData.documentMetadata.functionalGroupId
              }
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={formData.documentMetadata.regulatoryType}
            size="small"
            variant="outlined"
          />
        </div>
      )}
    </div>
  );
};

export default DocMetadata;
